import React, { useCallback, useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { AnimatedPageLoader } from 'smartsuite-ui';
import { getDataPromise, postDataPromise } from 'brock-fetch-utils';
import widgetDrawConfigurations from '../viewDashboards/widgets/widgetDrawConfigurations';
import LandingPage from './LandingPage';
import DashboardContext from '../shared/hooks/dashboardContext';
import {
    populateWidgetPanel,
    getInitialLandingPageConfig,
    applyLandingPageConfig,
    applyPopulatedPanels,
    populateSystemPanel,
} from './landingPageSetupUtils';
import mapConfigurationVersion from '../viewDashboards/utils/configVersionMappers/mapConfigurationVersion';
import { systemWidgetType } from './landingPageConstants';
import { useLabelContext } from '../shared/hooks/labelContext';
import { parseUserPreferenceValue } from '../shared/userPreferenceUtils';
import { mapSelectOptions } from '../shared/utilities/selectOptionsUtils';

export default function LandingPageLayout(props) {
    const { dashboardLabels } = useLabelContext();
    const [state, setState] = useState({});

    const setError = useCallback((error) => {
        setState((prevState) => ({ ...prevState, error }));
    }, []);

    const widgetRenderers = useMemo(
        () => ({
            ...widgetDrawConfigurations,
            ...props.customizations.widgetRenderers,
        }),
        [props.customizations.widgetRenderers]
    );

    useEffect(() => {
        function landingPagePromise() {
            return getDataPromise(
                props.uriConstants.getLandingPageConfigurationUri
            ).then(
                (result) => result,
                () => setError('Error loading landing page configuration')
            );
        }

        function canModifyLandingPagePromise() {
            return getDataPromise(props.uriConstants.canModifyLandingPage).then(
                (result) => result,
                () => {
                    // eslint-disable-next-line no-console
                    console.error(
                        'Error loading canModifyLandingPage privilege. Defaulting to false.'
                    );
                    return false;
                }
            );
        }

        function allDashboardConfigurationsPromise() {
            return getDataPromise(
                props.uriConstants.getAllDashboardConfigurations
            ).then(
                (result) => result,
                () => setError('Error loading all dashboard configurations')
            );
        }

        function userPreferencesPromise() {
            return getDataPromise(
                props.uriConstants.getAllUserPreferences
            ).then(
                (result) => result,
                () => setError('Error loading user preferences')
            );
        }

        function thresholdsPromise() {
            return getDataPromise(props.uriConstants.thresholdsUri).then(
                (result) => result,
                () => {
                    // eslint-disable-next-line no-console
                    console.error(
                        'Error loading thresholds. Defaulting to [].'
                    );
                    return [];
                }
            );
        }

        function selectOptionsPromise(apiKeys) {
            return postDataPromise(
                props.uriConstants.selectOptionsUri,
                apiKeys
            ).then(mapSelectOptions, () =>
                setError('Error loading select options')
            );
        }

        function initialize() {
            const promises = [
                landingPagePromise(),
                canModifyLandingPagePromise(),
                allDashboardConfigurationsPromise(),
                userPreferencesPromise(),
                thresholdsPromise(),
            ];

            Promise.all(promises).then(
                async ([
                    landingPageConfig,
                    canModifyLandingPage,
                    dashboardConfigs,
                    allUserPreferences,
                    thresholdValues,
                ]) => {
                    const initializedLandingPageConfig = applyLandingPageConfig(
                        getInitialLandingPageConfig(dashboardLabels),
                        landingPageConfig
                    );

                    const versionMappedDashboardConfigs = dashboardConfigs.map(
                        (dashboard) => ({
                            ...dashboard,
                            configuration: mapConfigurationVersion(
                                dashboard.configuration
                            ),
                        })
                    );

                    selectOptionsPromise().then((selectOptions) => {
                        const allSavedFilters = allUserPreferences
                            .map(parseUserPreferenceValue)
                            .filter((x) => x.savedDashboards?.length)
                            .map((x) => x.savedDashboards)
                            .flat();

                        const populatedPanels =
                            initializedLandingPageConfig.panels
                                .map((panel) => {
                                    if (
                                        panel.landingPageWidget?.type ===
                                        systemWidgetType
                                    ) {
                                        return populateSystemPanel(
                                            panel,
                                            widgetRenderers
                                        );
                                    }

                                    return populateWidgetPanel(
                                        panel,
                                        versionMappedDashboardConfigs,
                                        allSavedFilters,
                                        widgetRenderers,
                                        selectOptions
                                    );
                                })
                                .filter((x) => x);

                        const configuration = applyPopulatedPanels(
                            initializedLandingPageConfig,
                            populatedPanels
                        );

                        setState({
                            dashboardConfigs: versionMappedDashboardConfigs,
                            allSavedFilters,
                            canModifyLandingPage,
                            isLoaded: true,
                            configuration,
                            thresholdValues,
                            selectOptions,
                        });
                    });
                }
            );
        }

        initialize();
    }, [dashboardLabels, setError, props.uriConstants, widgetRenderers]);

    function renderLandingPage() {
        if (!state.isLoaded) {
            return <AnimatedPageLoader />;
        }

        return <LandingPage configuration={state.configuration} />;
    }

    if (state.error) {
        throw Error(state.error);
    }

    return (
        <DashboardContext.Provider
            value={{
                dashboardConfigs: state.dashboardConfigs,
                allSavedFilters: state.allSavedFilters,
                assets: props.assets,
                canModifyLandingPage: state.canModifyLandingPage,
                favoriteUtilities: props.favoriteUtilities,
                customizations: props.customizations,
                selectOptions: state.selectOptions,
                thresholdValues: state.thresholdValues,
                uriConstants: props.uriConstants,
                widgetRenderers,
            }}
        >
            {renderLandingPage()}
        </DashboardContext.Provider>
    );
}

LandingPageLayout.propTypes = {
    assets: PropTypes.instanceOf(Object).isRequired,
    customizations: PropTypes.instanceOf(Object).isRequired,
    favoriteUtilities: PropTypes.instanceOf(Object).isRequired,
    uriConstants: PropTypes.instanceOf(Object).isRequired,
};
